import React, { useCallback } from 'react'
import { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { dispatchAppEvent } from '@/src/renderer/lib/events'
import { AreaIcon } from '../../../../components/icons/AreaIcon'
import { PlusIcon } from '../../../../components/icons/PlusIcon'
import { useProjectsByLifeSpace } from '../../../../lib/db/hooks'
import { scheduleSync } from '../../../../lib/sync/sync-engine'
import { SidebarSection } from '../SidebarSection'
import { SidebarItem } from '../SidebarItem'
import { ProjectSidebarItem } from '../ProjectSidebarItem'
import { useProjectExpand } from '../useProjectExpand'
import type { ProjectsSectionProps } from './ProjectsSection.types'

export const ProjectsSection: React.FC<ProjectsSectionProps> = ({
  lifeSpace,
  activeTab,
  tabs,
  openTab,
  navigateActiveTab,
  onCloseTabByPath,
  onUnpinByPath,
  onPinByPath,
  openPaths,
  previewPaths,
  pinnedPaths,
  onOpenTab,
  onDoubleClick,
  onPromote,
  showTutorial,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: projects, createProject, deleteProject } = useProjectsByLifeSpace(lifeSpace)
  const { expandedProjects, toggleProjectExpand } = useProjectExpand(lifeSpace, activeTab, tabs)
  const activeSectionId = searchParams.get('section') || undefined

  const handleSectionClick = useCallback((projectId: string, sectionId: string) => {
    const projectPath = `/home/${lifeSpace}/project/${projectId}`
    if (activeTab?.path === projectPath) {
      navigate(`${projectPath}?section=${sectionId}`, { replace: true })
      dispatchAppEvent('scroll-to-section', sectionId)
    } else {
      navigateActiveTab({
        path: projectPath,
        title: projects?.find(p => p.id === projectId)?.name || t('project.defaultName'),
        icon: AreaIcon as unknown as LucideIcon,
      })
      queueMicrotask(() => navigate(`${projectPath}?section=${sectionId}`, { replace: true }))
    }
  }, [activeTab?.path, navigate, navigateActiveTab, projects, t, lifeSpace])

  const handleCreateProject = async () => {
    const project = await createProject({
      name: '',
      description: null,
      color: 'var(--space-500)',
      status: 'active',
      lifeSpace,
      startDate: null,
      endDate: null,
      progress: 0,
    })
    navigateActiveTab({
      path: `/home/${lifeSpace}/project/${project.id}`,
      title: t('project.defaultName'),
      icon: AreaIcon as unknown as LucideIcon,
    })
    scheduleSync()
  }

  const tutorialPath = `/home/${lifeSpace}/area/Erste-Schritte-in-HappyTask`

  return (
    <SidebarSection
      title={t('sidebar.projects')}
      defaultExpanded
      actionIcon={PlusIcon}
      onAction={handleCreateProject}
    >
      {showTutorial && (
        <SidebarItem
          icon={AreaIcon as unknown as LucideIcon}
          label={t('tutorial.projectName')}
          path={tutorialPath}
          isActive={activeTab?.path === tutorialPath}
          hasOpenTab={openPaths.has(tutorialPath)}
          isPreview={previewPaths.has(tutorialPath)}
          isPinned={pinnedPaths.has(tutorialPath)}
          onUnpin={onUnpinByPath ? () => onUnpinByPath(tutorialPath) : undefined}
          onPinTab={onPinByPath ? () => onPinByPath(tutorialPath) : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath(tutorialPath) : undefined}
          onOpenNewTab={() => openTab({ id: `${lifeSpace}-tutorial-${Date.now()}`, path: tutorialPath, title: t('tutorial.projectName'), icon: AreaIcon as unknown as LucideIcon, lifeSpace, isDirty: false, isPinned: false })}
          onClick={(e) => onOpenTab(e, `${lifeSpace}-tutorial`, tutorialPath, t('tutorial.projectName'), AreaIcon as unknown as LucideIcon)}
          onDoubleClick={() => onDoubleClick(tutorialPath)}
          onPromote={onPromote}
        />
      )}
      {projects?.map((project) => (
        <ProjectSidebarItem
          key={project.id}
          project={project}
          lifeSpace={lifeSpace}
          activeTab={activeTab}
          isExpanded={expandedProjects.has(project.id)}
          onToggleExpand={() => toggleProjectExpand(project.id)}
          openPaths={openPaths}
          previewPaths={previewPaths}
          pinnedPaths={pinnedPaths}
          onOpenTab={onOpenTab}
          onDoubleClick={onDoubleClick}
          onPromote={onPromote}
          onCloseTabByPath={onCloseTabByPath}
          onUnpinByPath={onUnpinByPath}
          onPinByPath={onPinByPath}
          openTab={openTab}
          onDelete={() => {
            const path = `/home/${lifeSpace}/project/${project.id}`
            onCloseTabByPath?.(path)
            deleteProject(project.id)
            scheduleSync()
          }}
          activeSectionId={activeSectionId}
          onSectionClick={handleSectionClick}
        />
      ))}
    </SidebarSection>
  )
}
